import { LitElement, html, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import { Keyword, getNaturalKeywordIdString } from "../model/keyword";
import { KeywordListWithKeywords } from "../model/keyword_list_with_keywords";
import { sortAlphabetAscIcon, sortAlphabetDescIcon, sortNumericAscIcon, sortNumericDescIcon, deleteIcon, copyFileIcon } from "../utils/icons";
import { Country, countryName, countryToFlagEmoji } from "../model/country";
import { Platform } from "../model/platform";
import { Store, KeywordSorting, KeywordSortProperty, SortDirection } from "../utils/store";
import { CountryFilter, countryFilterToCountry } from "../model/country_filter";
import { Api } from "../api";
import { router } from "../utils/routing";
import { maskText } from "../utils/utils";

/** Rows added per animation frame to keep the main thread responsive. */
const ROWS_PER_FRAME = 8;

/** Number of rows to render per batch (initial + each scroll-triggered load). */
const RENDER_BATCH_SIZE = 64;

@customElement("keyword-table-component")
export class KeywordTableComponent extends LitElement {
    @property({ type: Object }) list: KeywordListWithKeywords | null = null;
    @property({ type: Object }) countryFilter: CountryFilter = CountryFilter.UnitedStates;
    @property({ type: Object }) platform: Platform = Platform.iPhone;

    @state() private sorting: KeywordSorting | null = null;
    @state() private checkedKeywordIds: Set<number> = new Set();
    @state() private blurEnabled = false;

    /** How many keyword rows are currently rendered. */
    @state() private visibleCount = RENDER_BATCH_SIZE;

    /** Cached filtered + sorted keyword list. Recomputed in willUpdate(). */
    private cachedKeywords: Keyword[] = [];

    /** Observes the sentinel row at the bottom of the rendered list. */
    private scrollObserver: IntersectionObserver | null = null;

    /** rAF handle for the current trickle-render loop. */
    private trickleFrameId = 0;

    /** Target visibleCount the trickle loop is working towards. */
    private trickleTarget = 0;

    createRenderRoot() {
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        const sorting = Store.getSettings().sorting;
        if (sorting) {
            this.sorting = sorting;
        } else {
            this.sorting = {
                property: KeywordSortProperty.name,
                direction: SortDirection.ascending,
            };
        }

        this.blurEnabled = Store.getSettings().blurSensitiveData ?? false;

        // Listen for blur toggle events
        window.addEventListener("blur-toggle", this.handleBlurToggle);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener("blur-toggle", this.handleBlurToggle);
        this.scrollObserver?.disconnect();
        this.scrollObserver = null;
        cancelAnimationFrame(this.trickleFrameId);
    }

    willUpdate(changed: PropertyValues) {
        // Recompute cached keywords when the underlying data changes
        if (changed.has("list") || changed.has("countryFilter") || changed.has("platform") || changed.has("sorting")) {
            cancelAnimationFrame(this.trickleFrameId);
            this.trickleTarget = 0;
            this.visibleCount = RENDER_BATCH_SIZE;
            this.cachedKeywords = this.computeKeywords();
        }
    }

    updated() {
        this.observeSentinel();
    }

    /** Attach an IntersectionObserver to the sentinel row so more rows load on scroll. */
    private observeSentinel() {
        // Clean up previous observer
        this.scrollObserver?.disconnect();

        const total = this.keywords().length;
        if (this.visibleCount >= total) return;

        const sentinel = this.querySelector("[data-sentinel]");
        if (!sentinel) return;

        this.scrollObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    this.trickleTarget = Math.min(this.visibleCount + RENDER_BATCH_SIZE, this.keywords().length);
                    this.trickleRows();
                }
            },
            { rootMargin: "200px" }
        );
        this.scrollObserver.observe(sentinel);
    }

    /** Add ROWS_PER_FRAME rows each animation frame until the trickle target is reached. */
    private trickleRows() {
        if (this.visibleCount >= this.trickleTarget) return;
        this.trickleFrameId = requestAnimationFrame(() => {
            this.visibleCount = Math.min(this.visibleCount + ROWS_PER_FRAME, this.trickleTarget);
            this.trickleRows();
        });
    }

    private handleBlurToggle = (event: Event) => {
        const customEvent = event as CustomEvent;
        this.blurEnabled = customEvent.detail.enabled;
        this.requestUpdate();
    };

    private handleCheckboxChange(keywordId: number, checked: boolean) {
        if (checked) {
            this.checkedKeywordIds.add(keywordId);
        } else {
            this.checkedKeywordIds.delete(keywordId);
        }
        this.handleIndeterminate();
        this.requestUpdate();
    }

    private handleSelectAll(checked: boolean) {
        if (checked) {
            this.keywords().forEach((keyword) => this.checkedKeywordIds.add(keyword.id));
        } else {
            this.checkedKeywordIds.clear();
        }
        this.handleIndeterminate();
        this.requestUpdate();
    }

    private handleIndeterminate() {
        const selectAllCheckbox = document.getElementById("table-select-all") as HTMLInputElement;
        if (this.checkedKeywordIds.size === this.keywords().length) {
            selectAllCheckbox.indeterminate = false;
        } else if (this.checkedKeywordIds.size > 0) {
            selectAllCheckbox.indeterminate = true;
        } else {
            selectAllCheckbox.indeterminate = false;
        }
    }

    private keywords(): Keyword[] {
        return this.cachedKeywords;
    }

    private computeKeywords(): Keyword[] {
        const country = countryFilterToCountry(this.countryFilter);
        const keywords = (this.list?.keywords ?? []).filter((keyword) => {
            if (country) {
                return keyword.country === country && keyword.platform === this.platform;
            } else {
                return keyword.platform === this.platform;
            }
        });

        const sorting = this.sorting;
        if (sorting) {
            switch (sorting.property) {
                case KeywordSortProperty.name:
                    keywords.sort((a, b) => a.term.localeCompare(b.term) * sorting.direction);
                    break;
                case KeywordSortProperty.country:
                    keywords.sort((a, b) => a.country.localeCompare(b.country) * sorting.direction);
                    break;
                case KeywordSortProperty.popularity:
                    keywords.sort((a, b) => ((a.popularity ?? 0) - (b.popularity ?? 0)) * sorting.direction);
                    break;
                case KeywordSortProperty.difficulty:
                    keywords.sort((a, b) => ((a.difficulty ?? 0) - (b.difficulty ?? 0)) * sorting.direction);
                    break;
                case KeywordSortProperty.rank:
                    keywords.sort((a, b) => {
                        const appRankA = a.connectedAppRank ?? -1;
                        const appRankB = b.connectedAppRank ?? -1;

                        // Handle -1 values specially to always place them at the end
                        // regardless of sort direction
                        if (appRankA === -1 && appRankB !== -1) {
                            return 1; // A is -1, so it goes to the end
                        }
                        if (appRankA !== -1 && appRankB === -1) {
                            return -1; // B is -1, so it goes to the end
                        }
                        if (appRankA === -1 && appRankB === -1) {
                            return 0; // Both are -1, keep original order
                        }
                        return (appRankA - appRankB) * sorting.direction;
                    });
                    break;
                case KeywordSortProperty.delta:
                    keywords.sort((a, b) => {
                        const appDeltaA = this.delta(a);
                        const appDeltaB = this.delta(b);

                        // Handle -1 values specially to always place them at the end
                        // regardless of sort direction

                        if (appDeltaA !== undefined && appDeltaB !== undefined) {
                            return (appDeltaA - appDeltaB) * sorting.direction;
                        }
                        if (appDeltaA === undefined && appDeltaB !== undefined) {
                            return 1; // A is undefined, so it goes to the end
                        }
                        if (appDeltaA !== undefined && appDeltaB === undefined) {
                            return -1; // B is undefined, so it goes to the end
                        }
                        return 0; // Both are undefined, keep original order
                    });
                    break;
                case KeywordSortProperty.rankedApps:
                    keywords.sort((a, b) => ((a.ranksCount ?? 0) - (b.ranksCount ?? 0)) * sorting.direction);
                    break;
            }
        }
        return keywords;
    }

    private areAllSelected(): boolean {
        return this.keywords().length > 0 && this.checkedKeywordIds.size === this.keywords().length;
    }

    private isNoneSelected(): boolean {
        return this.checkedKeywordIds.size === 0;
    }

    private async handleDelete() {
        if (!confirm("Are you sure you want to delete the selected keywords?")) {
            return;
        }
        const keywordIdsToKeep = this.list?.keywords.filter((keyword) => !this.checkedKeywordIds.has(keyword.id)).map((keyword) => keyword.id);

        try {
            const result = await Api.updateListKeywordIds(this.list!.id, keywordIdsToKeep);
            if (result instanceof Error) {
                console.error(result);
            } else {
                this.list = result;
            }
            this.checkedKeywordIds.clear();
        } catch (error) {
            console.error(error);
        } finally {
            this.handleIndeterminate();
            this.requestUpdate();
        }
    }

    private handleCopy() {
        const keywords = this.keywords().filter((keyword) => this.checkedKeywordIds.has(keyword.id));
        const keywordsString = keywords.map((keyword) => keyword.term).join(",");
        navigator.clipboard.writeText(keywordsString);
        this.requestUpdate();
    }

    private sortBy(property: KeywordSortProperty) {
        const oldProperty = this.sorting?.property;
        let newDirection = (this.sorting?.direction ?? SortDirection.descending) * -1;
        if (oldProperty !== property) {
            newDirection = SortDirection.ascending;
        }
        this.sorting = {
            property: property,
            direction: newDirection,
        };

        Store.setSettings({
            ...Store.getSettings(),
            sorting: this.sorting,
        });
    }

    /** Resolve an app icon URL from the deduplicated iconsByAppId map. */
    private resolveAppIcon(appId: string): string | undefined {
        return this.list?.iconsByAppId?.[appId];
    }

    private delta(keyword: Keyword): number | undefined {
        if (keyword.connectedAppRank === undefined || keyword.connectedAppRankBefore === undefined) {
            return undefined;
        }
        return keyword.connectedAppRankBefore - keyword.connectedAppRank;
    }

    private deltaString(delta: number | undefined): string {
        if (delta === undefined) {
            return "--";
        }
        if (delta === 0) {
            return "=";
        }
        return delta > 0 ? `+${delta}` : `${delta}`;
    }

    render() {
        const allKeywords = this.keywords();
        const visibleKeywords = allKeywords.slice(0, this.visibleCount);
        const hasMore = this.visibleCount < allKeywords.length;
        return html`
            <div class="flex flex-col space-y-4">
                ${this.renderSelectionButtons()}
                <div class="overflow-x-auto rounded-box border border-base-content/5 bg-base-100">
                    <table class="table">
                        ${this.renderTableHeader()}
                        <tbody>
                            ${repeat(
                                visibleKeywords,
                                (keyword) => keyword.id,
                                (keyword) => this.renderTableRow(keyword)
                            )}
                            ${hasMore
                                ? html`<tr data-sentinel aria-hidden="true">
                                      <td colspan="100" class="text-center py-4 text-base-content/30 tracking-widest">•••</td>
                                  </tr>`
                                : ""}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    private renderSelectionButtons() {
        return html`
            <div class="flex flex-row items-center space-x-2">
                <button
                    class="btn ${this.isNoneSelected() ? "btn-ghost" : "btn-soft btn-primary"} btn-sm"
                    .disabled=${this.isNoneSelected()}
                    @click=${() => this.handleCopy()}
                >
                    <div class="w-4 h-4">${copyFileIcon}</div>
                    Copy
                </button>
                <button
                    class="btn ${this.isNoneSelected() ? "btn-ghost" : "btn-soft btn-error"} btn-sm"
                    .disabled=${this.isNoneSelected()}
                    @click=${() => this.handleDelete()}
                >
                    <div class="w-4 h-4">${deleteIcon}</div>
                    Delete
                </button>
            </div>
        `;
    }

    private renderTableHeader() {
        return html`
            <thead>
                <tr>
                    <th>
                        <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            id="table-select-all"
                            .checked=${this.areAllSelected()}
                            @change=${(e: Event) => this.handleSelectAll((e.target as HTMLInputElement).checked)}
                        />
                    </th>
                    <th class="w-full">
                        <div
                            class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property === KeywordSortProperty.name
                                ? "text-primary"
                                : ""}"
                            @click=${() => this.sortBy(KeywordSortProperty.name)}
                        >
                            <a class="w-full select-none">Keyword</a>
                            ${this.sorting?.property === KeywordSortProperty.name
                                ? this.sorting?.direction === SortDirection.ascending
                                    ? html`<span class="w-5 h-5">${sortAlphabetAscIcon}</span>`
                                    : html`<span class="w-5 h-5">${sortAlphabetDescIcon}</span>`
                                : ""}
                        </div>
                    </th>
                    ${this.countryFilter === CountryFilter.All
                        ? html`
                              <th>
                                  <div
                                      class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property ===
                                      KeywordSortProperty.country
                                          ? "text-primary"
                                          : ""}"
                                      @click=${() => this.sortBy(KeywordSortProperty.country)}
                                  >
                                      <a class="w-full select-none">Country</a>
                                      ${this.sorting?.property === KeywordSortProperty.country
                                          ? this.sorting?.direction === SortDirection.ascending
                                              ? html`<span class="w-5 h-5">${sortAlphabetAscIcon}</span>`
                                              : html`<span class="w-5 h-5">${sortAlphabetDescIcon}</span>`
                                          : ""}
                                  </div>
                              </th>
                          `
                        : html``}
                    ${this.list?.app
                        ? html`
                              <th>
                                  <div
                                      class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property === KeywordSortProperty.rank
                                          ? "text-primary"
                                          : ""}"
                                      @click=${() => this.sortBy(KeywordSortProperty.rank)}
                                  >
                                      <a class="w-full select-none text-center">Rank</a>
                                      ${this.sorting?.property === KeywordSortProperty.rank
                                          ? this.sorting?.direction === SortDirection.ascending
                                              ? html`<span class="w-5 h-5">${sortNumericAscIcon}</span>`
                                              : html`<span class="w-5 h-5">${sortNumericDescIcon}</span>`
                                          : ""}
                                  </div>
                              </th>
                              <th>
                                  <div
                                      class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property ===
                                      KeywordSortProperty.delta
                                          ? "text-primary"
                                          : ""}"
                                      @click=${() => this.sortBy(KeywordSortProperty.delta)}
                                  >
                                      <a class="w-full select-none text-center">Delta</a>
                                      ${this.sorting?.property === KeywordSortProperty.delta
                                          ? this.sorting?.direction === SortDirection.ascending
                                              ? html`<span class="w-5 h-5">${sortNumericAscIcon}</span>`
                                              : html`<span class="w-5 h-5">${sortNumericDescIcon}</span>`
                                          : ""}
                                  </div>
                              </th>
                          `
                        : html``}
                    <th>
                        <div
                            class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property === KeywordSortProperty.popularity
                                ? "text-primary"
                                : ""}"
                            @click=${() => this.sortBy(KeywordSortProperty.popularity)}
                        >
                            <a class="w-full select-none">Popularity</a>
                            ${this.sorting?.property === KeywordSortProperty.popularity
                                ? this.sorting?.direction === SortDirection.ascending
                                    ? html`<span class="w-5 h-5">${sortNumericAscIcon}</span>`
                                    : html`<span class="w-5 h-5">${sortNumericDescIcon}</span>`
                                : ""}
                        </div>
                    </th>
                    <th>
                        <div
                            class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property === KeywordSortProperty.difficulty
                                ? "text-primary"
                                : ""}"
                            @click=${() => this.sortBy(KeywordSortProperty.difficulty)}
                        >
                            <a class="w-full select-none">Difficulty</a>
                            ${this.sorting?.property === KeywordSortProperty.difficulty
                                ? this.sorting?.direction === SortDirection.ascending
                                    ? html`<span class="w-5 h-5">${sortNumericAscIcon}</span>`
                                    : html`<span class="w-5 h-5">${sortNumericDescIcon}</span>`
                                : ""}
                        </div>
                    </th>
                    <th>
                        <div
                            class="flex flex-row items-center space-x-2 cursor-pointer ${this.sorting?.property === KeywordSortProperty.rankedApps
                                ? "text-primary"
                                : ""}"
                            @click=${() => this.sortBy(KeywordSortProperty.rankedApps)}
                        >
                            <a class="w-full select-none">Apps</a>
                            ${this.sorting?.property === KeywordSortProperty.rankedApps
                                ? this.sorting?.direction === SortDirection.ascending
                                    ? html`<span class="w-5 h-5">${sortNumericAscIcon}</span>`
                                    : html`<span class="w-5 h-5">${sortNumericDescIcon}</span>`
                                : ""}
                        </div>
                    </th>
                    <th>
                        <a class="w-full select-none">Top Apps</a>
                    </th>
                </tr>
            </thead>
        `;
    }

    private handleClick(keyword: Keyword) {
        const appId = this.list?.appId;
        const appCountry = this.list?.appCountry;
        if (appId && appCountry) {
            const path = `/keyword/${getNaturalKeywordIdString(keyword)}?appId=${appId}&appCountry=${appCountry}`;
            router.push(path);
        } else {
            const path = `/keyword/${getNaturalKeywordIdString(keyword)}`;
            router.push(path);
        }
    }

    private renderTableRow(keyword: Keyword) {
        const delta = this.delta(keyword);
        const deltaString = this.deltaString(delta);

        return html`
            <tr class="hover:bg-base-200 cursor-pointer" @click=${() => this.handleClick(keyword)}>
                <td @click=${(e: Event) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        .checked=${this.checkedKeywordIds.has(keyword.id)}
                        @change=${(e: Event) => this.handleCheckboxChange(keyword.id, (e.target as HTMLInputElement).checked)}
                    />
                </td>
                <td>
                    <div class="flex flex-row items-center space-x-2">
                        <span class="text-lg ${this.blurEnabled ? "select-none" : ""}" style="${this.blurEnabled ? "filter: blur(4px);" : ""}">
                            ${this.blurEnabled ? maskText(keyword.term) : keyword.term}
                        </span>
                        ${keyword.syncing ? html`<div aria-label="status" class="status status-primary" />` : ""}
                    </div>
                </td>
                ${this.countryFilter === CountryFilter.All
                    ? html`
                          <td>
                              <div class="flex flex-row items-center space-x-2 md:space-x-4">
                                  <span class="text-lg">${countryToFlagEmoji(keyword.country)}</span>
                                  <span class="text-lg">${countryName(keyword.country)}</span>
                              </div>
                          </td>
                      `
                    : html``}
                ${this.list?.app
                    ? html`
                          <td
                              class="text-lg text-center ${keyword.connectedAppRank === 1
                                  ? "bg-amber-100"
                                  : keyword.connectedAppRank === 2
                                  ? "bg-slate-100"
                                  : keyword.connectedAppRank === 3
                                  ? "bg-orange-100"
                                  : keyword.connectedAppRank === undefined
                                  ? "text-gray-300"
                                  : ""}"
                          >
                              ${keyword.connectedAppRank ?? "--"}
                          </td>
                          <td
                              class="text-lg text-center ${delta && delta > 0 ? "text-primary" : delta && delta < 0 ? "text-error" : "text-gray-300"}"
                          >
                              ${deltaString}
                          </td>
                      `
                    : html``}
                <td>
                    ${keyword.popularity !== undefined
                        ? html`<div class="flex flex-row items-center space-x-2 md:space-x-4">
                              <progress
                                  class="progress ${keyword.popularity < 20
                                      ? "progress-error"
                                      : keyword.popularity < 60
                                      ? "progress-warning"
                                      : "progress-success"} w-12 md:w-16"
                                  value="${keyword.popularity}"
                                  max="100"
                              ></progress>
                              <span class="text-lg font-medium">${keyword.popularity}</span>
                          </div>`
                        : html`<span class="text-lg text-gray-300 font-medium">--</span>`}
                </td>
                <td>
                    ${keyword.difficulty !== undefined
                        ? html`<div class="flex flex-row items-center space-x-2 md:space-x-4">
                              <progress
                                  class="progress ${keyword.difficulty < 20
                                      ? "progress-success"
                                      : keyword.difficulty < 60
                                      ? "progress-warning"
                                      : "progress-error"} w-12 md:w-16"
                                  value="${keyword.difficulty}"
                                  max="100"
                              ></progress>
                              <span class="text-lg font-medium">${keyword.difficulty}</span>
                          </div>`
                        : html`<span class="text-lg text-gray-300 font-medium">--</span>`}
                </td>
                <td class="text-center text-lg">${keyword.ranksCount ?? 0}</td>
                <td>
                    <div class="flex flex-row items-center space-x-2" style="min-width:192px">
                        ${Array.from({ length: 5 }).map((_, index) => {
                            const rank = keyword.ranks?.[index];
                            const appIcon = rank ? this.resolveAppIcon(rank.appId) : undefined;
                            return appIcon
                                ? html`<img
                                      src=${appIcon}
                                      class="w-8 h-8 bg-base-100 rounded-lg border border-base-200"
                                      style="${this.blurEnabled ? "filter: blur(8px) saturate(1.5) brightness(1.1);" : ""}"
                                      loading="lazy"
                                  />`
                                : html`<div class="w-8 h-8 bg-base-100 rounded-lg border border-base-200"></div>`;
                        })}
                    </div>
                </td>
            </tr>
        `;
    }
}
